<template>
  <div class="{ 'with-floating-label': floatingLabel }">
    <div class="wrapper">
      <div class="c-input">
        <div v-if="label" class="c-input-label">
          {{ label }}
          <span v-if="requiredMark" class="c-input-label__required">*</span>
        </div>

        <div v-if="floatingLabel" class="c-input-floating-label-not-visible">
          {{ floatingLabel }}
          <span v-if="requiredMark" class="c-input-label__required">*</span>
        </div>

        <div class="c-input-wrapper">
          <textarea
            ref="textarea"
            v-model="textValue"
            class="c-input-textarea"
            :class="{
              'c-input-textarea_floating_label_active': textValue && floatingLabel || focused,
            }"
            rows="1"
            :maxlength="maxLength"
            :placeholder="placeholder"
            @focus="focused = true"
            @focusout="focused = false"
          >
          </textarea>

          <div v-if="floatingLabel" class="c-input-floating-label">
            {{ floatingLabel }}
            <span v-if="requiredMark" class="c-input-label__required">*</span>
          </div>

          <div class="c-input-actions">
            <div
              v-if="clearable"
              class="c-input-action c-input-action_clear"
              :class="{
                'c-input-action_clear_active': textValue,
              }"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg" @click="clear">
                <path
                  d="M0.325608 10.746C-0.0649162 10.3555 -0.0649162 9.72233 0.325608 9.33181L9.36312 0.292893C9.75364 -0.0976313 10.3868 -0.0976309 10.7773 0.292893C11.1679 0.683418 11.1679 1.31658 10.7773 1.70711L1.73982 10.746C1.3493 11.1365 0.716132 11.1365 0.325608 10.746Z"
                />
                <path
                  d="M10.6836 10.6835C11.0741 10.293 11.0741 9.65986 10.6836 9.26933L1.70711 0.292893C1.31658 -0.0976313 0.683417 -0.0976309 0.292893 0.292893C-0.0976311 0.683418 -0.0976311 1.31658 0.292893 1.70711L9.26935 10.6835C9.65987 11.0741 10.293 11.0741 10.6836 10.6835Z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div
          class="c-input-border"
          :class="{
            'c-input-border_active': focused,
            'c-input-border_required': invalid,
          }"
        ></div>
      </div>

      <div v-if="invalid && error" class="c-input-required c-input-required_active">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="7" cy="7" r="6.5" fill="white" stroke="#FF238D" />
          <circle cx="7" cy="10" r="1" fill="#FF238D" />
          <rect x="6" y="3" width="2" height="5" rx="1" fill="#FF238D" />
        </svg>

        <div class="c-input-required-text" v-html="error"></div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "c-input",

  props: [
    "value",
    "label",
    "floatingLabel",
    "invalid",
    "error",
    "placeholder",
    "clearable",
    "maxLength",
    "requiredMark",
  ],

  data() {
    return {
      textValue: "",
      focused: false,
    };
  },

  watch: {
    textValue(value) {
      this.$emit("input", value);

      this.setTextareaHeight("input");
    },

    value(value) {
      if (value === null) {
        this.textValue = "";
      } else {
        this.textValue = value;
      }

      this.setTextareaHeight("change value");
    },
  },

  created() {
    this.textValue = this.value;
  },

  mounted() {
    this.setTextareaHeight("mounted");
  },

  updated() {
    this.setTextareaHeight("updated");
  },

  methods: {
    setTextareaHeight() {
      const el = this.$refs.textarea;
      el.style.height = "auto";
      const newHeight = el.scrollHeight;
      if (newHeight === 0) {
        return;
      }
      el.style.height = `${newHeight}px`;
    },
    onInput(e) {
      this.textValue = e.target.innerText;
    },

    clear() {
      this.textValue = "";

      this.setTextareaHeight();
    },

    focus() {
      this.$refs.textarea.focus();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/vars";

.with-floating-label {
  padding-top: 20px;
}

.wrapper {
  position: relative;
}

.c-input {
  position: relative;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  justify-content: flex-start;
  max-height: 100%;
  overflow: hidden;

  &-required {
    display: flex;
    position: absolute;
    bottom: -20px;
    align-items: center;
    visibility: hidden;
    opacity: 0;
    transition: 0.2s;

    svg {
      flex-shrink: 0;
    }

    &_active {
      height: 14px;
      bottom: -22px;
      visibility: visible;
      opacity: 1;
    }

    &-text {
      font-size: 11px;
      color: $color-secondary;
      margin-left: 10px;
      margin-top: 1px;
    }
  }

  &-textarea {
    position: relative;
    width: 100%;
    padding: 0 0 13px 0;
    font-size: 15px;
    line-height: 108%;
    box-sizing: border-box;
    height: 30px;
    overflow-y: hidden;
    border: none;
    resize: none;

    &:focus {
      outline: none;
    }

    &_floating_label_active + .c-input-floating-label {
      font-size: 12px;
      //font-size: 80%;
      line-height: 13px;
      white-space: normal;
      transform: translate3d(0, -100%, 0);
      padding-right: 0;

      .c-input-label__required {
        visibility: visible;
      }

      .c-input-label-fullname {
        visibility: visible;
      }
    }
  }

  &-label {
    font: {
      size: 12px;
    }
    line-height: 1.2;
    margin-bottom: 10px;
    color: $color-label;

    &__required {
      color: red;
      transform: translateX(-3px);
    }
  }

  &-floating-label {
    position: absolute;
    overflow: hidden !important;
    max-width: 100%;
    white-space: nowrap;
    text-overflow: ellipsis;
    left: 0;
    font: 400 15px Fira Sans;
    pointer-events: none;
    line-height: 18px;
    color: $color-label;
    padding-bottom: 10px;
    transition: all 200ms;

    &-pr {
      padding-right: 15px;
    }
  }

  &-floating-label-not-visible {
    font: {
      size: 12px;
    }
    visibility: hidden;
    line-height: 13px;
    margin-bottom: 10px;
    color: $color-label;
  }

  &-wrapper {
    width: 100%;
    display: flex;
    flex-grow: 1;
    overflow: auto;
  }

  &-value {
    width: calc(100% - 22px);
    height: 30px;
    font-size: 15px;
    padding: 0 0 13px 0;
    cursor: pointer;
    order: 2;

    &_placeholder {
      color: $color-label;
    }

    &_type {
      &_select {
        cursor: pointer;

        &_multiple {
          height: auto;
          min-height: 30px;
          padding-bottom: 0;
        }
      }
    }

    &-placeholder {
      padding-top: 5px;
      margin-left: 5px;
      color: $color-label;
    }
  }

  textarea {
    &::placeholder {
      color: $color-label;
    }
  }

  &-input {
    border: none;
    width: 100%;
    font-size: 15px;
    padding: 0 0 10px 0;
    min-height: 30px;
    order: 2;

    &[disabled] {
      background: white;
    }

    &:focus {
      outline: none;

      & + .c-input-border {
        background: $color-primary;
      }
    }

    &::placeholder {
      color: $color-label;
    }

    &_placeholder {
      &_italic {
        &::placeholder {
          font-style: italic;
        }
      }
    }
  }

  &-actions {
    display: flex;
    order: 3;
    align-items: flex-start;

    position: sticky;
    top: 0;
  }

  &-action {
    margin-left: 10px;

    &_clear {
      margin-top: -3px;
      transition: 0.2s;
      opacity: 0;
      visibility: hidden;
      cursor: pointer;

      svg {
        fill: $color-secondary;
      }

      &_active {
        opacity: 1;
        visibility: visible;
      }
    }
  }

  &-checkbox {
    order: 4;
  }

  &-border {
    height: 1px;
    width: 100%;
    position: absolute;
    background: $color-control-border;
    bottom: 0;
    transition: 0.2s;

    &_active {
      background: $color-primary;
    }

    &_required {
      background: $color-pink;
    }
  }
}
</style>
